import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import Navbar from "../components/Navbar";
import CodeSimulatorSection from "../components/CodeSimulatorSection";
import BootcampSection from "../components/BootcampSection";
import ProblemsSection from "../components/ProblemsSection";
import AIInterviewTeaser from "../components/AIInterviewTeaser";
import FAQSection from "../components/FAQSection";
import CTABanner from "../components/CTABanner";
import Footer from "../components/Footer";
import DynamicBadge from "../components/DynamicBadge";
import SEO from "../components/SEO";
import { getBootcampStatus } from "../lib/bootcamp";

import {
  Video,
  FileText,
  Trophy,
  Users,
  ChevronRight,
  Play,
  BookOpen,
  Code,
  Zap,
  Award,
  Target,
  ArrowRight,
  Rocket,
  Star,
  Brain,
  Plus,
  Terminal,
  Activity,
  Globe,
  Shield,
  Cpu,
  TrendingUp,
  CheckCircle2,
  Sparkles,
  GraduationCap,
  LineChart
} from "lucide-react";

import HeroBackground from "../components/HeroBackground";

const Home = () => {
  const [bootcampStatus, setBootcampStatus] = useState(null);
  const trackRef = useRef(null);
  const [activeStop, setActiveStop] = useState(0);

  useEffect(() => {
    getBootcampStatus().then(data => setBootcampStatus(data));
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    
    const items = Array.from(track.children);
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.55) {
          const idx = items.indexOf(entry.target);
          if (idx !== -1 && idx < 6) { 
             setActiveStop(Math.min(idx, 5)); // 5 dots for 5 actual cards
          }
        }
      });
    }, { root: track, threshold: [0.55] });

    items.forEach(item => observer.observe(item));

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="bg-background text-foreground font-sora min-h-screen overflow-x-hidden" data-testid="home-page">
      <Navbar />
      <SEO 
        title="Learn Python, Master AI/ML Online & Practice Coding | DevHireLab"
        description="Master Python with free interactive tutorials, daily coding challenges, and an online compiler. Get job-ready with our AI/ML mock interviews and Zero to Hero bootcamp."
      />

      {/* ─── HERO SECTION ─── */}
      <section className="bg-background relative min-h-screen flex flex-col items-center justify-center pt-24 md:pt-32 pb-16 md:pb-20 overflow-hidden">
        
        <HeroBackground variant="home" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 flex flex-col items-center text-center">
          {/* Dynamic Eyebrow badge */}
          <DynamicBadge />

          {/* Headline */}
          <motion.h1 
            initial={{ opacity: 0, y: 24 }} 
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] text-white tracking-tighter mb-8 max-w-5xl uppercase"
          >
            Learn Python. <br />
            <span className="text-primary italic" style={{ textShadow: '0 0 15px rgba(0, 230, 118, 0.2)' }}>Build Real Skills.</span> <br />
            Get <span className="amber-underline">Hired.</span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            className="font-sora text-muted-foreground text-[clamp(1rem,3vw,1.25rem)] max-w-2xl leading-relaxed mb-16"
          >
            The all-in-one platform for mastering Python through interactive tutorials, live simulations, and AI-powered interviews.
          </motion.p>

          {/* Journey Path Concept - break out of max-w-7xl so path SVG spans full width */}
        </div>
        <div className="journey-wrap" style={{ width: '100%' }}>
            <svg className="path-svg" viewBox="0 0 1200 260" preserveAspectRatio="none">
              <path d="M 100 130 C 220 40, 300 40, 340 130 C 420 220, 500 220, 560 130 C 640 40, 700 40, 780 130 C 850 210, 900 190, 970 150 C 1030 120, 1070 100, 1110 90"
                    stroke="var(--border)" strokeWidth="2" fill="none" strokeDasharray="2 10" strokeLinecap="round"/>
              {/* Animated dash traveling along the path */}
              <path d="M 100 130 C 220 40, 300 40, 340 130 C 420 220, 500 220, 560 130 C 640 40, 700 40, 780 130 C 850 210, 900 190, 970 150 C 1030 120, 1070 100, 1110 90"
                    stroke="var(--primary)" strokeWidth="1.5" fill="none" strokeDasharray="0 1200" strokeLinecap="round" opacity="0.5">
                <animate attributeName="stroke-dasharray" from="0 1200" to="1200 0" dur="3s" begin="0.5s" fill="freeze" />
              </path>
            </svg>

            <div className="track" ref={trackRef}>
              {/* Stop 1: Tutorials */}
              <div className="stop">
                <div className="journey-node" style={{ "--node-color": "var(--primary)" }}></div>
                <Link to="/tutorials" className="journey-card" style={{ "--card-accent": "var(--primary)" }}>
                  <div className="journey-art">
                    <span className="journey-tag tag-free">FREE</span>
                    <svg viewBox="0 0 320 150" fill="none">
                      <rect x="70" y="35" width="180" height="14" rx="3" fill="#1c2a35"/>
                      <rect x="70" y="35" width="90" height="14" rx="3" fill="#2ee6a6" opacity="0.85"/>
                      <rect x="70" y="60" width="180" height="10" rx="3" fill="#1c2a35"/>
                      <rect x="70" y="78" width="150" height="10" rx="3" fill="#1c2a35"/>
                      <rect x="70" y="96" width="180" height="10" rx="3" fill="#1c2a35"/>
                      <circle cx="255" cy="80" r="22" fill="none" stroke="#2ee6a6" strokeWidth="2" opacity="0.6"/>
                      <path d="M247 80 L253 86 L264 72" stroke="#2ee6a6" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="journey-card-body">
                    <h3>Free Tutorials</h3>
                    <p>Structured lessons from basics to advanced.</p>
                  </div>
                </Link>
              </div>

              {/* Stop 2: Problems */}
              <div className="stop">
                <div className="journey-node" style={{ "--node-color": "var(--primary)" }}></div>
                <Link to="/problems" className="journey-card" style={{ "--card-accent": "var(--primary)" }}>
                  <div className="journey-art">
                    <span className="journey-tag tag-free">FREE</span>
                    <svg viewBox="0 0 320 150" fill="none">
                      <rect x="60" y="30" width="200" height="90" rx="8" fill="#0d1420" stroke="#1c2a35"/>
                      <text x="76" y="55" fill="#5f6b73" fontFamily="monospace" fontSize="11">def solve(arr):</text>
                      <text x="88" y="72" fill="#2ee6a6" fontFamily="monospace" fontSize="11">return sorted(arr)</text>
                      <rect x="76" y="82" width="60" height="8" rx="2" fill="#1c2a35"/>
                      <rect x="76" y="96" width="90" height="8" rx="2" fill="#1c2a35"/>
                      <circle cx="228" cy="100" r="14" fill="none" stroke="#2ee6a6" strokeWidth="2"/>
                      <path d="M222 100 L226 104 L235 94" stroke="#2ee6a6" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="journey-card-body">
                    <h3>Coding Problems</h3>
                    <p>200 curated algorithms & daily challenges.</p>
                  </div>
                </Link>
              </div>

              {/* Stop 3: Code Simulator */}
              <div className="stop">
                <div className="journey-node" style={{ "--node-color": "var(--primary)" }}></div>
                <Link to="/playground" className="journey-card" style={{ "--card-accent": "var(--primary)" }}>
                  <div className="journey-art">
                    <span className="journey-tag tag-free">FREE</span>
                    <svg viewBox="0 0 320 150" fill="none">
                      <rect x="60" y="40" width="14" height="60" rx="3" fill="#1c2a35"/>
                      <rect x="82" y="55" width="14" height="45" rx="3" fill="#1c2a35"/>
                      <rect x="104" y="30" width="14" height="70" rx="3" fill="#2ee6a6" opacity="0.9"/>
                      <rect x="126" y="48" width="14" height="52" rx="3" fill="#1c2a35"/>
                      <rect x="148" y="65" width="14" height="35" rx="3" fill="#1c2a35"/>
                      <line x1="60" y1="112" x2="200" y2="112" stroke="#1c2a35" strokeWidth="2"/>
                      <polygon points="215,60 215,90 240,75" fill="#2ee6a6" opacity="0.85"/>
                      <circle cx="255" cy="75" r="26" fill="none" stroke="#1c2a35" strokeWidth="2"/>
                    </svg>
                  </div>
                  <div className="journey-card-body">
                    <h3>Code Simulator</h3>
                    <p>Visualize execution line-by-line.</p>
                  </div>
                </Link>
              </div>

              {/* Stop 4: AI/ML Bootcamp */}
              <div className="stop">
                <div className="journey-node" style={{ "--node-color": "var(--accent)" }}></div>
                <Link to="/bootcamp" className="journey-card" style={{ "--card-accent": "var(--accent)" }}>
                  <div className="journey-art">
                    <span className="journey-tag tag-soon">{bootcampStatus?.status === 'enrolling' ? 'ENROLLING' : 'SOON'}</span>
                    <svg viewBox="0 0 320 150" fill="none">
                      <path d="M50 110 Q120 110 150 75 T270 40" stroke="rgba(245,166,35,0.25)" strokeWidth="3" fill="none" strokeDasharray="6 6"/>
                      <circle cx="50" cy="110" r="7" fill="#f5a623"/>
                      <circle cx="150" cy="75" r="7" fill="rgba(245,166,35,0.2)" stroke="#f5a623" strokeWidth="2"/>
                      <circle cx="210" cy="55" r="7" fill="rgba(245,166,35,0.2)" stroke="#f5a623" strokeWidth="2"/>
                      <circle cx="270" cy="40" r="10" fill="#f5a623"/>
                      <path d="M266 40 L269 43 L275 35" stroke="#0a0f18" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="journey-card-body">
                    <h3>AI/ML Bootcamp</h3>
                    <p>Project-driven path to job readiness.</p>
                  </div>
                </Link>
              </div>

              {/* Stop 5: AI Interview */}
              <div className="stop">
                <div className="journey-node" style={{ "--node-color": "var(--accent)" }}></div>
                <Link to="/ai-interview" className="journey-card" style={{ "--card-accent": "var(--accent)" }}>
                  <div className="journey-art">
                    <span className="journey-tag tag-soon">SOON</span>
                    <svg viewBox="0 0 320 150" fill="none">
                      <rect x="55" y="40" width="110" height="40" rx="10" fill="#1c2a35"/>
                      <path d="M75 80 L65 95 L95 80 Z" fill="#1c2a35"/>
                      <rect x="150" y="70" width="115" height="40" rx="10" fill="rgba(245,166,35,0.18)"/>
                      <path d="M245 110 L255 125 L225 110 Z" fill="rgba(245,166,35,0.18)"/>
                      <circle cx="207" cy="50" r="16" fill="none" stroke="#f5a623" strokeWidth="2"/>
                      <path d="M200 50 a7 7 0 0 1 14 0 M203 45 v-4 M211 45 v-4" stroke="#f5a623" strokeWidth="1.6" fill="none" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div className="journey-card-body">
                    <h3>AI Interview</h3>
                    <p>Ace interviews with real-time AI feedback.</p>
                  </div>
                </Link>
              </div>

              {/* Finish Node */}
              <div className="journey-finish">
                <div className="journey-node"></div>
                <div className="label">🎉 Hired.</div>
                <div className="sub">You made it.</div>
              </div>
            </div>

            {/* Mobile Scroll Indicators */}
            <div className="dots-mobile">
              {[0, 1, 2, 3, 4, 5].map((idx) => (
                <span key={idx} className={`dot-ind ${activeStop === idx ? 'active' : ''}`} />
              ))}
            </div>
          </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 flex flex-col items-center text-center">

          {/* CTAs */}
          <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full sm:w-auto">
            <Link to="/tutorials" className="w-full sm:w-auto">
              <Button variant="outline" size="lg" className="btn-outline w-full sm:w-auto">
                EXPLORE TUTORIALS →
              </Button>
            </Link>
            <Link to="/bootcamp" className="w-full sm:w-auto">
              <Button size="lg" className="btn-primary w-full sm:w-auto">
                {bootcampStatus?.status === 'enrolling' ? 'JOIN THE BOOTCAMP' : 'VIEW BOOTCAMP'}
              </Button>
            </Link>
          </motion.div>

          {/* Social Proof */}
          <div className="mt-20 flex flex-row flex-wrap items-center justify-center gap-10 text-muted-foreground text-[10px] font-semibold uppercase tracking-[0.2em]">
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Growing Community</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Hands-On Projects</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> No Credit Card</span>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── FREE TUTORIALS SECTION ─── */}
      <section className="theme-light bg-background relative py-20 md:py-32 overflow-hidden">
        {/* Subtle Dotted Background */}
        <div 
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle, #0E9B6B 2px, transparent 2px)", backgroundSize: "32px 32px" }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 flex flex-col items-center">
          
          <motion.div className="text-center mb-24 max-w-3xl">
            <div className="border-primary-subtle bg-primary-subtle inline-flex items-center gap-2 px-3 py-1.5 border mb-8">
              <BookOpen className="w-3.5 h-3.5 text-primary" />
              <span className="text-[9px] font-semibold text-primary uppercase tracking-[0.3em]">Free Education</span>
            </div>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-6 leading-[1] uppercase tracking-tighter">
              Learn Python the <span className="text-primary italic">Professional Way</span>
            </h2>
            <p className="font-sora text-muted-foreground text-lg leading-relaxed max-w-2xl mx-auto">
              Skip the fluff. Get straight to building <span className="amber-underline">production-grade</span> applications with our curated roadmap.
            </p>
          </motion.div>

          {/* Editorial Mockup */}
          <motion.div className="w-full mb-24 theme-dark">
            <Card className="p-0 overflow-hidden border-border atm-card text-foreground">
              <div className="bg-card border-b border-border flex items-center justify-between px-6 py-4 relative z-10">
                <div className="flex gap-2">
                  <div className="w-3 h-3 bg-red-500/20" />
                  <div className="w-3 h-3 bg-yellow-500/20" />
                  <div className="w-3 h-3 bg-primary/20" />
                </div>
                <div className="bg-background hidden sm:flex flex-1 max-w-md mx-auto h-8 items-center px-4">
                  <span className="text-muted-foreground text-[10px] truncate uppercase tracking-widest">devhirelab.dev/masterclass</span>
                </div>
                <div className="pulsing-dot" />
              </div>

              <div className="flex flex-col lg:flex-row min-h-[600px] relative z-10">
                {/* Left Sidebar */}
                <div className="bg-card border-r border-border hidden lg:flex w-80 flex-col p-10">
                  <span className="text-[10px] text-primary font-semibold uppercase tracking-[0.3em] mb-10">Course Roadmap</span>
                  <div className="space-y-8">
                    {["Syntax & Basics", "Control Flow", "Data Structures", "Functions", "OOP Mastery"].map((t, i) => (
                      <div key={i} className="flex items-start gap-5 group cursor-pointer">
                        <span className={`text-[10px] font-semibold ${i < 3 ? 'text-primary' : 'text-muted-foreground'}`}>0{i+1}</span>
                        <span className={`text-xs font-semibold uppercase tracking-tight ${i < 3 ? 'text-white' : 'text-muted-foreground group-hover:text-white transition-colors'}`}>{t}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Main Content */}
                <div className="bg-background flex-1 p-10 md:p-20 relative overflow-hidden">
                  <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: "radial-gradient(#00E676 1px, transparent 1px)", backgroundSize: "16px 16px" }} />
                  <div className="relative z-10 max-w-3xl">
                    <span className="text-primary text-[10px] font-semibold mb-6 block uppercase tracking-[0.3em]">Module 05 · Production Grade</span>
                    <h3 className="text-2xl md:text-3xl font-semibold text-foreground mb-10 leading-tight uppercase tracking-tight">Object Oriented <br /> Architecture</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                      <div className="space-y-6">
                        <p className="font-sora text-muted-foreground text-sm leading-relaxed">Object-oriented programming (OOP) is a paradigm centered around "objects" rather than "functions". It makes complex code modular and scalable.</p>
                        <div className="bg-card border-l-[3px] border-l-[#00E676] p-6 relative">
                          <p className="font-sora text-xs text-primary font-medium leading-relaxed italic">"Think of classes as blueprints and objects as the actual physical entities."</p>
                          <div className="absolute top-2 right-2 w-1 h-1 bg-primary opacity-30" />
                        </div>
                      </div>
                      <div className="bg-card border-border p-6 border font-mono text-[11px] leading-relaxed text-primary relative overflow-hidden group">
                        <div className="absolute inset-0 bg-gradient-to-b from-[#00E676]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                        <span className="text-white opacity-50"># Define a professional architecture</span><br/>
                        <span className="text-white">class</span> Developer:<br/>
                        &nbsp;&nbsp;<span className="text-white">def</span> __init__(self, name):<br/>
                        &nbsp;&nbsp;&nbsp;&nbsp;self.name = name<br/>
                        &nbsp;&nbsp;<span className="text-white">def</span> code(self):<br/>
                        &nbsp;&nbsp;&nbsp;&nbsp;return <span className="text-accent">"Writing Python..."</span><span className="animate-pulse">|</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          <Link to="/tutorials" className="w-full sm:w-auto">
            <Button size="lg" className="btn-primary w-full sm:w-auto h-20 px-16 text-xl">
              Start Learning Free →
            </Button>
          </Link>
        </div>
      </section>

      <div className="section-divider" />
      <ProblemsSection />
      <div className="section-divider" />
      <CodeSimulatorSection />
      <div className="section-divider" />
      <BootcampSection />
      <div className="section-divider" />
      <AIInterviewTeaser />
      <div className="section-divider" />
      <FAQSection />
      <div className="section-divider" />
      <CTABanner />
      <Footer />
    </div>
  );
};

export default Home;
